'use client';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useEffect, useRef, useState } from 'react';

const THUMB_WIDTH = 64;
const THUMB_GAP = 10;
const THUMB_STEP = THUMB_WIDTH + THUMB_GAP;
/** Thumbnails kept in the DOM beyond each edge of the strip, so a scroll does not show gaps. */
const OVERSCAN = 4;

const thumbClassName =
  'w-16 flex-none cursor-pointer rounded-[6px] border-2 border-transparent bg-transparent p-0 opacity-60 transition-all duration-200 hover:border-[#00a1e6] hover:opacity-100';

type CatalogueThumbnailsProps = {
  pageCount: number;
  page: number;
  urls: ReadonlyMap<number, string>;
  request: ((pageNumbers: number[]) => void) | undefined;
  onSelect: (pageNumber: number) => void;
  className?: string;
};

/**
 * Strip of page thumbnails. Catalogues have up to ~650 pages, so only the thumbnails in view (and a
 * few around them) are in the DOM, and only those in view are rendered from the PDF.
 */
export function CatalogueThumbnails({
  pageCount,
  page,
  urls,
  request,
  onSelect,
  className,
}: CatalogueThumbnailsProps) {
  const stripRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState({ first: 1, last: 0 });

  useEffect(() => {
    const strip = stripRef.current;
    if (!strip || !pageCount) return;

    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const first = Math.max(1, Math.floor(strip.scrollLeft / THUMB_STEP) + 1);
        const last = Math.min(
          pageCount,
          Math.ceil((strip.scrollLeft + strip.clientWidth) / THUMB_STEP),
        );
        setVisible(current =>
          current.first === first && current.last === last ? current : { first, last },
        );
      });
    };
    const observer = new ResizeObserver(update);

    observer.observe(strip);
    strip.addEventListener('scroll', update, { passive: true });

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      strip.removeEventListener('scroll', update);
    };
  }, [pageCount]);

  // Each request replaces the previous one: thumbnails scrolled past are not rendered.
  useEffect(() => {
    if (visible.last < visible.first) return;
    request?.(
      Array.from({ length: visible.last - visible.first + 1 }, (_, i) => visible.first + i),
    );
  }, [visible, request]);

  // Keeps the current page's thumbnail in the middle of the strip.
  useEffect(() => {
    const strip = stripRef.current;
    strip?.scrollTo({
      left: (page - 1) * THUMB_STEP - (strip.clientWidth - THUMB_WIDTH) / 2,
      behavior: 'smooth',
    });
  }, [page]);

  const first = Math.max(1, visible.first - OVERSCAN);
  const last = Math.min(pageCount, visible.last + OVERSCAN);

  return (
    <div
      ref={stripRef}
      className={cn(
        'relative col-span-full flex gap-2.5 overflow-x-auto px-0.5 pb-1 pt-[30px] [scrollbar-width:thin]',
        className,
      )}
    >
      {/* Spacers stand in for the thumbnails outside the rendered range and keep the scroll width. */}
      {first > 1 && (
        <span
          aria-hidden
          className="flex-none"
          style={{ width: (first - 1) * THUMB_STEP - THUMB_GAP }}
        />
      )}
      {Array.from({ length: Math.max(0, last - first + 1) }, (_, index) => {
        const pageNumber = first + index;
        const url = urls.get(pageNumber);
        return (
          <button
            key={pageNumber}
            type="button"
            data-page={pageNumber}
            aria-label={`${pageNumber} / ${pageCount}`}
            aria-current={pageNumber === page}
            onClick={() => onSelect(pageNumber)}
            className={cn(thumbClassName, pageNumber === page && 'border-[#00a1e6] opacity-100')}
          >
            <span className="block aspect-[130/177] w-full overflow-hidden rounded-[4px] bg-[#f3f5f7]">
              {url && (
                // Object URL of a page rendered by pdf.js – nothing for the image optimizer.
                // eslint-disable-next-line @next/next/no-img-element
                <img src={url} alt="" className="h-full w-full object-cover" />
              )}
            </span>
          </button>
        );
      })}
      {last < pageCount && (
        <span
          aria-hidden
          className="flex-none"
          style={{ width: (pageCount - last) * THUMB_STEP - THUMB_GAP }}
        />
      )}
    </div>
  );
}
